import { defineStore } from 'pinia';
import { useAuthStore } from '@/store/auth';
import { useVoipSocketStore } from '@/store/voipSocket';
import { getAgents } from '@/api/agents.api';

/** Normalize to allowed statuses only: idle, offline, on_call */
function normalizeStatus(wsStatus) {
  if (!wsStatus) return 'offline';
  const status = String(wsStatus).toLowerCase();
  if (status === 'idle' || status === 'available') return 'idle';
  if (status === 'active') return 'active';
  if (status === 'on_call' || status === 'busy' || status === 'in_call')
    return 'on_call';
  return 'offline';
}

export const useAgentStatusStore = defineStore('agentStatus', {
  state: () => ({
    agentStatuses: {},
    agentList: [],
    loadingAgents: false,
    _unsubscribeOpen: null,
    _unsubscribeMessage: null,
  }),

  getters: {
    getAgentStatus: (state) => (agentId) => {
      if (!agentId) return 'offline';
      const id = typeof agentId === 'number' ? agentId : parseInt(agentId, 10);
      return state.agentStatuses[id] || 'offline';
    },
    getStatusLabel: () => (status) => {
      switch (status) {
        case 'idle':
          return 'Idle';
        case 'on_call':
          return 'On call';
        case 'offline':
          return 'Offline';
        default:
          return 'Offline';
      }
    },
    getStatusColor: () => (status) => {
      switch (status) {
        case 'idle':
          return 'bg-green-500';
        case 'on_call':
          return 'bg-red-500';
        case 'offline':
          return 'bg-gray-400';
        default:
          return 'bg-gray-400';
      }
    },
  },

  actions: {
    _onOpen() {
      const authStore = useAuthStore();
      const user = authStore.user;
      const agentId = user?.id != null ? Number(user.id) : null;
      const label = user?.name ?? '';
      if (agentId != null && !Number.isNaN(agentId)) {
        useVoipSocketStore().send({
          type: 'agent_status',
          agent_id: agentId,
          status: 'idle',
          label,
        });
      }
    },

    _onMessage(data) {
      if (data.type === 'agent_status' && data.agent_id != null) {
        const agentId = parseInt(data.agent_id, 10);
        if (!isNaN(agentId)) {
          this.agentStatuses = {
            ...this.agentStatuses,
            [agentId]: normalizeStatus(data.status),
          };
        }
      }
    },

    /**
     * Register agent-status handlers with the voip socket (idempotent).
     * Call once before connecting; the single connect is useVoipSocketStore().connect().
     */
    registerWithVoipSocket() {
      if (this._unsubscribeOpen) return;
      const socket = useVoipSocketStore();
      this._unsubscribeOpen = socket.registerOpenHandler(() => this._onOpen());
      this._unsubscribeMessage = socket.registerMessageHandler((data) =>
        this._onMessage(data)
      );
    },

    /**
     * Fetch agents from /api/agents and replace both agentList and agentStatuses.
     * Call on initial load of Advance settings and when returning to the tab so
     * the store is always overridden with server truth. WebSocket updates then
     * merge into agentStatuses in real time until the next fetch.
     */
    async fetchAgents() {
      this.loadingAgents = true;
      try {
        const response = await getAgents();
        const list = response?.data || response || [];
        const arr = Array.isArray(list) ? list : [];
        this.agentList = arr.map((a) => ({
          id: a.id,
          name: a.name || a.username || `User ${a.id}`,
          username: a.username,
          status: a.status,
          has_ongoing_campaign: Boolean(a.has_ongoing_campaign),
        }));
        // Override whole agentStatuses from API (initial load + leave/return)
        const statuses = {};
        arr.forEach((a) => {
          if (a.id != null) {
            const id = typeof a.id === 'number' ? a.id : parseInt(a.id, 10);
            if (!Number.isNaN(id)) {
              statuses[id] = normalizeStatus(a.status);
            }
          }
        });
        this.agentStatuses = statuses;
      } catch (error) {
        console.error('Error fetching agents:', error);
        this.agentList = [];
      } finally {
        this.loadingAgents = false;
      }
    },
  },
});
